import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  FileText,
  Home,
  BookOpen,
  BarChart3,
  Zap,
  Code,
  Cpu,
  ChevronDown,
  ExternalLink,
  DollarSign,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./GFSLayout.css";

function GFSLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [apiDocsExpanded, setApiDocsExpanded] = useState(false);
  const [costData, setCostData] = useState({ v1: 0, v2: 0, total: 0 });

  // Fetch 30-day cost data
  useEffect(() => {
    const fetchCostData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const [v1Res, v2Res] = await Promise.all([
          fetch("/v1/gfs/api/usage?days=30&version=v1", { headers }),
          fetch("/v1/gfs/api/usage?days=30&version=v2", { headers }),
        ]);
        
        const v1Data = v1Res.ok ? await v1Res.json() : null;
        const v2Data = v2Res.ok ? await v2Res.json() : null;
        
        const v1Cost = v1Data?.summary?.total_cost || 0;
        const v2Cost = v2Data?.summary?.total_cost || 0;
        
        setCostData({
          v1: v1Cost,
          v2: v2Cost,
          total: v1Cost + v2Cost,
        });
      } catch (err) {
        console.error("Failed to fetch cost data:", err);
      }
    };
    
    fetchCostData();
  }, []);

  // Get API Docs URLs for GFS endpoints
  const getApiDocsUrl = (version) => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    return `${baseUrl}/api/extraction/gfs/${version}/docs`;
  };

  // API docs items for submenu
  const apiDocsItems = [
    { url: getApiDocsUrl('v1'), label: 'GFS V1', description: 'Textract + Qwen', color: '#3b82f6', className: 'textract' },
    { url: getApiDocsUrl('v2'), label: 'GFS V2', description: 'Tesseract + Qwen VL', color: '#8b5cf6', className: 'qwen' },
  ];

  const menuItems = [
    { path: "/gfs", icon: Home, label: "Dashboard", exact: true },
    { path: "/gfs/v1/work-orders", icon: Zap, label: "V1 Work Orders", color: "#3b82f6", description: "Textract + Qwen" },
    { path: "/gfs/v2/work-orders", icon: Cpu, label: "V2 Work Orders", color: "#8b5cf6", description: "Tesseract + Qwen VL" },
    { path: "/gfs/usage", icon: BarChart3, label: "Usage & Costs", color: "#10b981" },
    { path: "/gfs/api-logs", icon: Code, label: "API Logs" },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path || location.pathname === item.path + "/";
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/gfs" || location.pathname === "/gfs/") {
      return "GFS Dashboard";
    }
    if (location.pathname.includes("/v1/work-order/")) {
      return "V1 Work Order Details";
    }
    if (location.pathname.includes("/v2/work-order/")) {
      return "V2 Work Order Details";
    }
    if (location.pathname.includes("/work-order/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.description ? `${item.label} - ${item.description}` : item.label;
    }
    return "GFS TA Ops";
  };

  return (
    <div className="gfs-layout">
      {/* Sidebar */}
      <aside className="gfs-sidebar">
        <div className="gfs-sidebar-header">
        {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
          <button onClick={() => navigate("/dashboard")} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",margin:"0 12px 8px",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:6,cursor:"pointer",fontSize:12,color:"#475569",fontWeight:500}} title="Back to Main Dashboard">
            <span style={{fontSize:14}}>←</span> Main Dashboard
          </button>
        )}
          <div className="gfs-logo">
            <FileText size={28} className="gfs-logo-icon" />
            <span className="gfs-logo-text">GFS</span>
          </div>
          <div className="gfs-logo-subtitle">Manulife OCR Pipeline</div>
        </div>

        <nav className="gfs-sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`gfs-nav-item ${isActive(item) ? "active" : ""}`}
              onClick={() => navigate(item.path)}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="gfs-nav-item-content">
                <span className="gfs-nav-label">{item.label}</span>
                {item.description && <span className="gfs-nav-description">{item.description}</span>}
              </div>
            </button>
          ))}

          {/* API Docs with Expandable Submenu */}
          <div className="gfs-nav-submenu-wrapper">
            <button
              className={`gfs-nav-item gfs-nav-item-external ${apiDocsExpanded ? 'expanded' : ''}`}
              onClick={() => setApiDocsExpanded(!apiDocsExpanded)}
              title="API Docs"
            >
              <BookOpen size={20} />
              <div className="gfs-nav-item-content">
                <span className="gfs-nav-label">API Docs</span>
                <span className="gfs-nav-description">Swagger UI</span>
              </div>
              <ChevronDown size={16} className={`gfs-nav-chevron ${apiDocsExpanded ? 'rotated' : ''}`} />
            </button>

            {/* Submenu Items */}
            <div className={`gfs-nav-submenu ${apiDocsExpanded ? 'expanded' : ''}`}>
              {apiDocsItems.map((doc) => (
                <button
                  key={doc.url}
                  className={`gfs-nav-submenu-item ${doc.className}`}
                  onClick={() => window.open(doc.url, "_blank")}
                  style={{ '--item-accent': doc.color }}
                >
                  <span className="gfs-submenu-dot" style={{ background: doc.color }}></span>
                  <div className="gfs-submenu-content">
                    <span className="gfs-submenu-label">{doc.label}</span>
                    <span className="gfs-submenu-desc">{doc.description}</span>
                  </div>
                  <ExternalLink size={14} className="gfs-submenu-external-icon" />
                </button>
              ))}
            </div>
          </div>
        </nav>

        <div className="gfs-sidebar-footer">
          {/* 30-Day Cost Summary */}
          <div className="gfs-sidebar-cost-summary">
            <div className="gfs-cost-header">
              <DollarSign size={16} />
              <span className="gfs-cost-title">30-Day Costs</span>
            </div>
            <div className="gfs-cost-items">
              <div className="gfs-cost-item">
                <span className="gfs-cost-label" style={{ color: '#3b82f6' }}>Textract + Qwen</span>
                <span className="gfs-cost-value">${costData.v1.toFixed(4)}</span>
              </div>
              <div className="gfs-cost-item">
                <span className="gfs-cost-label" style={{ color: '#8b5cf6' }}>Tesseract + Qwen VL</span>
                <span className="gfs-cost-value">${costData.v2.toFixed(4)}</span>
              </div>
              <div className="gfs-cost-item total">
                <span className="gfs-cost-label">Total</span>
                <span className="gfs-cost-value">${costData.total.toFixed(4)}</span>
              </div>
            </div>
          </div>

          <button
            className="gfs-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="gfs-nav-item-content">
              <span className="gfs-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="gfs-layout-content">
        {/* Top Header */}
        <header className="gfs-top-header">
          <div className="gfs-header-title">{getPageTitle()}</div>

          <div className="gfs-header-actions">
            <NotificationsPanel />
            
            <div className="gfs-user-menu">
              <button
                className="gfs-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="gfs-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || "G"}
                </div>
                <span className="gfs-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="gfs-user-dropdown">
                  <div className="gfs-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="gfs-dropdown-name">{user?.name || "GFS User"}</div>
                      <div className="gfs-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="gfs-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="gfs-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default GFSLayout;
